import React, { useCallback, useEffect, useState } from "react";
import {
  Loader2,
  RefreshCw,
  LogOut,
  Smartphone,
  Tablet,
  Monitor,
  Tv as TvIcon,
  Globe,
  Crown,
  Shield,
  AlertTriangle,
  Pencil,
  Check,
  X,
} from "lucide-react";
import { useAuth, authHeaders } from "./auth";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/+$/, "")}/api`;

function timeAgo(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  return `${Math.floor(diff / 86400)}d ago`;
}

function fullDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleString(undefined, {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function DeviceIcon({ kind }) {
  const cls = "text-heda-ink/85";
  switch (kind) {
    case "Mobile":
      return <Smartphone size={18} className={cls} strokeWidth={1.5} />;
    case "Tablet":
      return <Tablet size={18} className={cls} strokeWidth={1.5} />;
    case "TV":
      return <TvIcon size={18} className={cls} strokeWidth={1.5} />;
    default:
      return <Monitor size={18} className={cls} strokeWidth={1.5} />;
  }
}

export default function Sessions() {
  const { user: me, logout } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState("");
  const [busyId, setBusyId] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setErr("");
    try {
      const res = await fetch(`${API}/sessions`, { headers: authHeaders() });
      const data = await res.json();
      setItems(data.items || []);
    } catch {
      setErr("Failed to load sessions.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const revoke = async (s) => {
    const youSelf = s.is_current;
    if (
      !window.confirm(
        youSelf
          ? "This is THIS device — you'll be logged out immediately. Continue?"
          : `Sign out ${s.user_name}'s "${s.device_label}"?`
      )
    )
      return;
    setBusyId(s.id);
    try {
      const r = await fetch(`${API}/sessions/${s.id}`, {
        method: "DELETE",
        headers: authHeaders(),
      });
      if (!r.ok) {
        const d = await r.json().catch(() => ({}));
        alert(d.detail || "Could not revoke session");
        return;
      }
      if (youSelf) {
        logout();
        return;
      }
      setItems((prev) => prev.filter((x) => x.id !== s.id));
    } finally {
      setBusyId("");
    }
  };

  const revokeAllForUser = async (userId, userName) => {
    if (!window.confirm(`Sign out ALL devices of ${userName}?`)) return;
    const r = await fetch(`${API}/sessions/revoke-user/${userId}`, {
      method: "POST",
      headers: authHeaders(),
    });
    if (r.ok) load();
  };

  const grouped = items.reduce((acc, s) => {
    (acc[s.user_id] = acc[s.user_id] || []).push(s);
    return acc;
  }, {});

  return (
    <div data-testid="sessions-view">
      <div className="mb-4 flex items-center justify-between">
        <div className="text-[12px] uppercase tracking-wider text-heda-mute">
          {items.length} active device{items.length === 1 ? "" : "s"}
          {me.role === "owner" && " · across all users"}
        </div>
        <button
          data-testid="sessions-refresh"
          onClick={load}
          className="inline-flex items-center gap-1.5 rounded-full border border-heda-line bg-white px-3 py-1.5 text-[12px] text-heda-ink hover:border-heda-gold"
        >
          <RefreshCw size={12} className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      <div className="mb-5 flex items-start gap-2 rounded-xl border border-heda-gold/30 bg-heda-gold/5 px-4 py-3 text-[12.5px] text-heda-ink/80">
        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-heda-gold" />
        <div>
          See an unfamiliar device? Tap <span className="font-semibold">Sign out</span> to instantly
          revoke that login. The user will be logged out everywhere on that device. New logins
          appear here within seconds.
        </div>
      </div>

      {err && (
        <div className="mb-4 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {err}
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-heda-line bg-white py-12">
          <Loader2 size={22} className="animate-spin text-heda-gold" />
        </div>
      ) : items.length === 0 ? (
        <div className="flex flex-col items-center justify-center rounded-3xl border border-dashed border-heda-line bg-white py-16 text-center">
          <Globe size={28} className="mb-3 text-heda-mute" strokeWidth={1.4} />
          <h3 className="font-display text-lg font-bold text-heda-ink">No active sessions</h3>
          <p className="mt-1 text-sm text-heda-mute">No one is currently logged in.</p>
        </div>
      ) : (
        <div className="space-y-5">
          {Object.entries(grouped).map(([uid, sessions]) => {
            const u = sessions[0];
            return (
              <div
                key={uid}
                data-testid={`sessions-user-${uid}`}
                className="overflow-hidden rounded-2xl border border-heda-line bg-white"
              >
                <div className="flex items-center justify-between gap-2 border-b border-heda-line bg-heda-cream/50 px-4 py-2.5">
                  <div className="flex items-center gap-2">
                    <span
                      className={[
                        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider",
                        uid === me.id
                          ? "bg-heda-gold/15 text-heda-gold"
                          : "bg-heda-olive-tint text-heda-olive-dark",
                      ].join(" ")}
                    >
                      {uid === me.id ? <Crown size={10} /> : <Shield size={10} />}
                      {sessions.length} device{sessions.length === 1 ? "" : "s"}
                    </span>
                    <div className="font-semibold text-heda-ink">{u.user_name}</div>
                    <div className="hidden text-[11.5px] text-heda-mute sm:block">
                      {u.user_email}
                    </div>
                  </div>
                  {me.role === "owner" && sessions.length > 1 && (
                    <button
                      data-testid={`revoke-all-${uid}`}
                      onClick={() => revokeAllForUser(uid, u.user_name)}
                      className="text-[11px] font-medium text-red-600 hover:underline"
                    >
                      Sign out all
                    </button>
                  )}
                </div>
                <ul className="divide-y divide-heda-line">
                  {sessions.map((s) => (
                    <SessionRow
                      key={s.id}
                      s={s}
                      busyId={busyId}
                      onRevoke={() => revoke(s)}
                      onRenamed={(newNick) => {
                        setItems((prev) =>
                          prev.map((x) => (x.id === s.id ? { ...x, nickname: newNick } : x))
                        );
                      }}
                    />
                  ))}
                </ul>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SessionRow({ s, busyId, onRevoke, onRenamed }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(s.nickname || "");
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async () => {
    setErr("");
    setSaving(true);
    try {
      const r = await fetch(`${API}/sessions/${s.id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json", ...authHeaders() },
        body: JSON.stringify({ nickname: draft.trim() }),
      });
      const d = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(d.detail || "Could not save");
      onRenamed(draft.trim());
      setEditing(false);
    } catch (e) {
      setErr(e.message);
    } finally {
      setSaving(false);
    }
  };

  const primary = s.nickname || s.device_label;
  const subtitle = s.nickname ? s.device_label : null;

  return (
    <li
      data-testid={`session-${s.id}`}
      className="flex items-center justify-between gap-3 px-4 py-3"
    >
      <div className="flex min-w-0 flex-1 items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-full border border-heda-line bg-heda-cream/50">
          <DeviceIcon kind={s.device_kind} />
        </div>
        <div className="min-w-0 flex-1">
          {editing ? (
            <div className="flex items-center gap-1.5">
              <input
                data-testid={`nickname-input-${s.id}`}
                autoFocus
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") save();
                  if (e.key === "Escape") {
                    setEditing(false);
                    setDraft(s.nickname || "");
                  }
                }}
                placeholder="e.g. Sneha's iPhone 15"
                className="w-full max-w-xs rounded-lg border border-heda-gold/40 bg-white px-2 py-1 text-[14px] outline-none focus:border-heda-gold"
                maxLength={60}
              />
              <button
                data-testid={`nickname-save-${s.id}`}
                onClick={save}
                disabled={saving}
                className="rounded-full bg-heda-ink p-1.5 text-white hover:bg-black disabled:opacity-50"
              >
                {saving ? <Loader2 size={11} className="animate-spin" /> : <Check size={11} />}
              </button>
              <button
                onClick={() => {
                  setEditing(false);
                  setDraft(s.nickname || "");
                }}
                className="rounded-full border border-heda-line p-1.5 text-heda-mute hover:bg-heda-cream/40"
              >
                <X size={11} />
              </button>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-[14px] font-semibold text-heda-ink">{primary}</span>
              <button
                data-testid={`nickname-edit-${s.id}`}
                onClick={() => setEditing(true)}
                className="rounded-full p-1 text-heda-mute hover:bg-heda-cream/60 hover:text-heda-gold"
                title="Rename device"
              >
                <Pencil size={11} />
              </button>
              {s.is_current && (
                <span className="rounded-full bg-heda-olive/15 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-heda-olive-dark">
                  this device
                </span>
              )}
            </div>
          )}
          <div className="mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5 text-[11.5px] text-heda-mute">
            {subtitle && <span className="text-heda-ink/70">{subtitle}</span>}
            <span>Last active: <span className="text-heda-ink/85">{timeAgo(s.last_seen_at)}</span></span>
            <span>Signed in: {fullDate(s.created_at)}</span>
            {s.ip && <span>IP: {s.ip}</span>}
          </div>
          {err && (
            <div className="mt-1 text-[11px] text-red-600">{err}</div>
          )}
        </div>
      </div>
      <button
        data-testid={`revoke-${s.id}`}
        onClick={onRevoke}
        disabled={busyId === s.id}
        className="inline-flex shrink-0 items-center gap-1 rounded-full border border-heda-line px-3 py-1.5 text-[12px] font-medium text-heda-mute transition hover:border-red-300 hover:bg-red-50 hover:text-red-600 disabled:opacity-50"
      >
        {busyId === s.id ? <Loader2 size={11} className="animate-spin" /> : <LogOut size={11} />}
        Sign out
      </button>
    </li>
  );
}
